import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { endpoints, Url } from '@rossum/api-client';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@rossum/ui/material';
import { useApiClient } from '../../lib/apiClient';
import { canViewDocumentHistorySelector } from '../../redux/modules/organizationGroup/selectors';
import { CustomRelationDrawer } from '../custom-relations/drawer/CustomRelationDrawer';
import { CommentsDrawerContainer } from '../document-comments/comments-thread';
import { DocumentHistoryDrawer } from '../document-history';
import {
  SelectAnnotationParams,
  SupportedAnnotationView,
} from '../document-list-base/supportedAnnotationViews';
import { WorkflowActivityDrawer } from './activity-drawer/WorkflowActivityDrawer';
import { AttachmentsDrawer } from './annotation-attachment/AttachmentsDrawer';
import { DuplicatesDrawer } from './annotation-duplicates/DuplicatesDrawer';
import { DocumentEmailDrawer } from './DocumentEmailDrawer';
import { FilesDrawer } from './files/FileDrawer';
import { useFetchFiles } from './files/hooks/useFetchFiles';

type DocumentDrawerProps = {
  onClose: () => void;
  selectedAnnotationId: number | undefined;
  selectedAnnotationView: SupportedAnnotationView | undefined;
  selectedRelationId?: number;
  handleSelectAnnotation: (params: SelectAnnotationParams) => void;
  onAnnotationDelete?: (annotation: Url) => void;
};

export const DocumentDrawer = ({
  selectedAnnotationId,
  selectedAnnotationView,
  selectedRelationId,
  onClose,
  handleSelectAnnotation,
  onAnnotationDelete,
}: DocumentDrawerProps) => {
  const api = useApiClient();
  const canViewDocumentHistory = useSelector(canViewDocumentHistorySelector);

  const { data, isError, isSuccess } = useQuery({
    queryKey: ['annotation', selectedAnnotationId],
    enabled: selectedAnnotationId !== undefined,
    queryFn: () => {
      return selectedAnnotationId
        ? api.request(
            endpoints.annotations.list({
              id: [selectedAnnotationId],
            })
          )
        : null;
    },
  });

  const selectedAnnotation = data?.results?.[0];
  const documentRelations = useFetchFiles(selectedAnnotation?.id ?? null);

  const intl = useIntl();

  return isError || (isSuccess && !selectedAnnotation) ? (
    <Dialog open>
      <DialogTitle
        title={intl.formatMessage({
          id: 'components.modal.annotationNotFound.title',
        })}
      />
      <DialogContent sx={{ m: 3, p: 0, mb: 0 }}>
        <Typography>
          {intl.formatMessage({
            id: 'components.modal.annotationNotFound.text',
          })}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" color="secondary">
          {intl.formatMessage({
            id: 'components.modal.annotationNotFound.cancel',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  ) : selectedAnnotation ? (
    <>
      {selectedAnnotationView === 'attachments' ? (
        <AttachmentsDrawer
          onClose={onClose}
          annotationUrl={selectedAnnotation.url}
          handleSelectAnnotation={handleSelectAnnotation}
        />
      ) : null}
      {selectedAnnotationView === 'duplicates' ? (
        <DuplicatesDrawer
          onClose={onClose}
          parentAnnotationUrl={selectedAnnotation.url}
          handleSelectAnnotation={handleSelectAnnotation}
          onAnnotationDelete={onAnnotationDelete}
        />
      ) : null}
      {selectedAnnotationView === 'activities' ? (
        <WorkflowActivityDrawer
          annotation={selectedAnnotation}
          onClose={onClose}
        />
      ) : null}
      {selectedAnnotationView === 'emails' ? (
        <DocumentEmailDrawer
          emailThreadUrl={selectedAnnotation.emailThread}
          queueUrl={selectedAnnotation.queue}
          onClose={onClose}
        />
      ) : null}
      {selectedAnnotationView === 'files' && documentRelations ? (
        <FilesDrawer
          annotation={selectedAnnotation}
          documentRelations={documentRelations}
          onClose={onClose}
        />
      ) : null}
      {selectedAnnotationView === 'comments' ? (
        <CommentsDrawerContainer
          annotation={selectedAnnotation}
          onClose={onClose}
        />
      ) : null}
      {selectedAnnotationView === 'customRelation' ? (
        <CustomRelationDrawer
          onClose={onClose}
          relationId={selectedRelationId ?? null}
          annotationUrl={selectedAnnotation.url}
          handleSelectAnnotation={handleSelectAnnotation}
        />
      ) : null}
      {selectedAnnotationView === 'documentHistory' &&
      canViewDocumentHistory ? (
        <DocumentHistoryDrawer
          annotationId={selectedAnnotation.id}
          schemaUrl={selectedAnnotation.schema}
          open
          onClose={onClose}
        />
      ) : null}
    </>
  ) : null;
};
